// src/layouts/Pages/PageHeader/DropdownButtonMenu.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo } from 'react';
import { Button, Dropdown, Tooltip, theme } from 'antd';
import translateX from 'utils/translateX';
import { MoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useModuleContext } from 'components/core/ModuleProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DropdownButtonMenu = ({
  // Identification
  action,

  // Button appearance
  icon,
  tooltip,
  type,
  disabled,

  // Dropdown items
  dropdownItems = [],

  // Context (passed by ActionsButton)
  actions = [],
  actionContext,
  _actionConfig,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const moduleContext = useModuleContext();
  const {
    setDocumentDuplicated,
    setDocumentState,
    documentState,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handleListRefresh,
  } = moduleContext;

  // Get form-related values from SimpleFormContext (may be undefined if not inside SimpleFormProvider)
  // IMPORTANT: DropdownButtonMenu may be OUTSIDE the SimpleFormProvider tree (e.g., in PageHeader)
  // In that case, useSimpleFormContext() returns {}, so we fallback to moduleContext.formContext
  // which is registered by SimpleFormProvider via registerProviderContext('formContext', ...)
  const directFormContext = useSimpleFormContext();
  const formContext = directFormContext?.form ? directFormContext : moduleContext?.formContext;
  const submitForm = formContext?.submitForm;
  const resetForm = formContext?.resetForm;
  const locked = formContext?.locked;
  const submitting = formContext?.submitting;
  const working = formContext?.working;

  const { viewMode: mode, setViewMode: setMode } = useDocumentContext();

  const storedCurrentPage = useZustand('module', 'currentPage');
  const setPageStore = useZustand('module', 'setPage');
  const setPageDataStore = useZustand('module', 'setPageData');
  const storedListSettings = useZustand('module', 'listSettings');
  const storedPageSettings = useZustand('module', 'pageSettings');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedCompany = useZustand('auth', 'company');
  const apiGlobal = useZustand('global', 'useApi');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const fallbackNavigate = useNavigate();
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = actionContext?.navigate || fallbackNavigate;

  // Check if we're in a DocumentContext or ListContext
  const isDocumentContext = !!moduleContext?.documentContext;

  const isViewMode = documentState?.mode === 'view';

  // Check visibility based on actions array
  const showDropdownMenu = actions.some(
    a =>
      a === 'dropdownMenu' ||
      (typeof a === 'object' &&
        (a.action === 'dropdownMenu' || a.name === 'dropdownMenu' || a.type === 'dropdownMenu'))
  );

  // Button is always enabled unless explicitly disabled via prop
  const isDisabled =
    typeof disabled === 'function'
      ? disabled({ page: storedCurrentPage, moduleContext })
      : Boolean(disabled);

  // Build menu items from dropdownItems
  const menuItems = useMemo(
    () => buildMenuItems(dropdownItems, storedCurrentPage, moduleContext, storedSubscription),
    [dropdownItems, storedCurrentPage, moduleContext, storedSubscription]
  );

  const hasAnyItems = menuItems.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Build context object to pass to handlers
  const buildHandlerContext = () => {
    // Get functions from listContext
    const listContextRefresh = moduleContext?.listContext?.refresh;
    const listContextClearFilters = moduleContext?.listContext?.clearFilters;
    const listContextResetToDefaults = moduleContext?.listContext?.resetToDefaults;
    const listContextHasActiveFilters = moduleContext?.listContext?.hasActiveFilters;
    const documentContextRefresh = moduleContext?.documentContext?.refresh || apiGlobal?.refresh;

    const context = {
      // Functions from ModuleContext
      submit: submitForm,
      navigate,
      resetForm,
      setDocumentState,
      setDocumentDuplicated,
      handleArchiveDocuments,
      handleRemoveDocuments,
      handleRestoreDocuments,
      handleListRefresh,
      setMode,

      // Refresh (from listContext or documentContext or apiGlobal)
      refresh: listContextRefresh || documentContextRefresh,
      refreshDocument: documentContextRefresh,
      refreshList: listContextRefresh,

      // List filters (from listContext)
      clearFilters: listContextClearFilters,
      resetToDefaults: listContextResetToDefaults,
      hasActiveFilters: listContextHasActiveFilters,

      // API
      apiGlobal,

      // Store functions
      setPageData: setPageDataStore,

      // Current state
      page: storedCurrentPage,
      listSettings: storedListSettings,
      pageSettings: storedPageSettings,
      documentState,
      mode,

      // Auth state
      storedCompany,
      storedSubscription,

      // Utilities
      setPage: setPageStore,
      config: _actionConfig,
      actionContext,
      moduleContext,
    };

    return context;
  };

  const handleMenuClick = ({ key }) => {
    const item = (dropdownItems || []).find(i => i && i.key === key);

    if (item && typeof item.onClick === 'function') {
      // Use requestAnimationFrame + setTimeout to ensure:
      // 1. React has finished its current render cycle
      // 2. Dropdown close animation has started
      // This prevents race conditions on first open when components are initializing
      requestAnimationFrame(() => {
        setTimeout(() => {
          appService.notificationDestroy();
          item.onClick(buildHandlerContext());
        }, 100);
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonIcon = icon || <MoreOutlined />;
  const buttonType = type;
  const title = translateX(tooltip || 'actions');

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!showDropdownMenu || !hasAnyItems) return null;

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleMenuClick }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Tooltip title={title} placement="top">
        <Button
          type={buttonType}
          style={isDisabled ? { backgroundColor: '#ffffff' } : undefined}
          disabled={isDisabled}
          icon={buttonIcon}
        />
      </Tooltip>
    </Dropdown>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DropdownButtonMenu;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildMenuItems(dropdownItems, currentPage, moduleContext, storedSubscription) {
  if (!Array.isArray(dropdownItems) || dropdownItems.length === 0) return [];

  // First pass: filter out hidden items and normalize dividers
  const filteredItems = dropdownItems
    .filter(i => {
      // Support string 'divider' or object with type: 'divider'
      if (i === 'divider' || (i && i.type === 'divider')) return true;

      // Must be an object with a key
      if (!i || typeof i !== 'object' || !i.key) return false;

      // Handle hidden property - if hidden returns true, filter out the item
      if (typeof i.hidden === 'function') {
        return !i.hidden({ currentPage, moduleContext, storedSubscription });
      }
      return !i.hidden;
    })
    .map(i => {
      // Normalize string 'divider' to object
      if (i === 'divider') return { type: 'divider' };
      return i;
    });

  // Second pass: remove orphan dividers (no visible items above or below)
  const cleanedItems = filteredItems.filter((item, index, arr) => {
    // If not a divider, keep it
    if (item.type !== 'divider') return true;

    // Check if there's a non-divider item above
    const hasItemAbove = arr.slice(0, index).some(i => i.type !== 'divider');

    // Check if there's a non-divider item below
    const hasItemBelow = arr.slice(index + 1).some(i => i.type !== 'divider');

    // Only show divider if there are visible items both above AND below
    return hasItemAbove && hasItemBelow;
  });

  // Third pass: map to final menu item format
  return cleanedItems.map(i => {
    // Handle divider type
    if (i.type === 'divider') {
      return { type: 'divider' };
    }

    const isDanger = !!i.danger;
    const baseLabel = translateX(i.label) || i.label;

    // If disabled is a function, evaluate in real time
    let isDisabled =
      typeof i.disabled === 'function'
        ? i.disabled({ currentPage, moduleContext, storedSubscription })
        : !!i.disabled;

    const iconNode = i.icon ? React.cloneElement(i.icon, {}) : undefined;

    return {
      key: String(i.key),
      label: baseLabel,
      icon: iconNode,
      disabled: isDisabled,
      danger: isDanger,
    };
  });
}
